import {
  FormProvider,
  Grid,
  Stack,
  Alert,
  ReadOnlyField,
  DropzoneReadonly,
  GridItem,
  FiFileText,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { getUrlFileFromAws } from '../../../../services/aws/getUrlFileFromAws';
import {
  DocumentationAndFormsFormProps,
  useDocumentationAndFormsForm,
} from './hooks';
import { DocumentSection } from '../../../shared/DocumentSection';
import { MultipleDocumentSection } from '../../../shared/MultipleDocumentSection';

export const DocumentationAndFormsForm = ({
  data,
  refetch,
}: DocumentationAndFormsFormProps) => {
  const { t } = useTranslation();
  const {
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    allowFormEdition,
    isFormReadOnly,
    actionButtons,
    fileBoxNumber,
  } = useDocumentationAndFormsForm({ data, refetch });

  return (
    <>
      <form noValidate>
        <FormProvider {...formMethods}>
          <Stack gap={8}>
            {isPendingChangeRequest && (
              <Alert
                title={t('explorer.changeRequest.alert.title')}
                description={validationMessage}
                variant="error"
              />
            )}
            <Grid templateColumns={{ lg: 'repeat(2, 1fr)' }} gap={6}>
              <DocumentSection
                isDocumentRequired
                documentName="sisaFile"
                label={t('common.pages.docsAndForms.sisaFile.label')}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
              />
              <DocumentSection
                mt={14}
                documentName="statuteFile"
                isDocumentRequired={false}
                label={t('common.pages.docsAndForms.statuteFile.label')}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
              />
              <MultipleDocumentSection
                mt={14}
                name="statuteChangeFiles"
                isDocumentRequired={false}
                label={t('common.pages.docsAndForms.statuteChangeFiles.label')}
                additionalLabel={t(
                  'common.pages.docsAndForms.additionalStatuteChangeFiles.label',
                )}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
                shouldHideInitialField
              />
              <MultipleDocumentSection
                mt={14}
                name="assemblyBoardMinutesFiles"
                isDocumentRequired={false}
                label={t(
                  'common.pages.docsAndForms.assemblyBoardMinutesFiles.label',
                )}
                additionalLabel={t(
                  'common.pages.docsAndForms.additionalAssemblyBoardMinutesFiles.label',
                )}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
              />
              <MultipleDocumentSection
                mt={14}
                name="powerOfAttorneyFiles"
                isDocumentRequired={false}
                label={t(
                  'common.pages.docsAndForms.powerOfAttorneyFiles.label',
                )}
                additionalLabel={t(
                  'common.pages.docsAndForms.additionalPowerOfAttorneyFiles.label',
                )}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
              />

              <GridItem mt={8}>
                <ReadOnlyField
                  label={t('common.pages.docsAndForms.boxNumber.label')}
                  value={fileBoxNumber ?? t('common.toBeCompletedByBolsa')}
                />
              </GridItem>
              <MultipleDocumentSection
                mt={14}
                name="additionalDocumentFiles"
                isDocumentRequired={false}
                label={t('common.pages.docsAndForms.additionalDocument.label')}
                additionalLabel={t(
                  'common.pages.docsAndForms.additionalDocument.addNew.label',
                )}
                hideDelete={!allowFormEdition}
                isReadonly={isFormReadOnly}
              />
              <GridItem colSpan={{ lg: 2 }} mt={8}>
                <DropzoneReadonly
                  fileName={data.signupDocument?.originalFileName || ''}
                  label={t(
                    'common.pages.docsAndForms.businessSingupForm.label',
                  )}
                  onClick={async () => {
                    if (data.signupDocument?.uuid) {
                      const url = await getUrlFileFromAws(
                        data.signupDocument?.uuid,
                      );
                      window.open(url, '_blank');
                    }
                  }}
                  leftIcon={FiFileText}
                />
              </GridItem>
            </Grid>
            {actionButtons}
          </Stack>
        </FormProvider>
      </form>
    </>
  );
};
